/**
 * Draft entity contract - shared shape for locally-created entities that
 * have not yet been submitted to the CRM event log.
 */

import type { OverwriteTarget } from '@/types/crm';

import type { EventBuilder } from './EventBuilder';
import { isValidEntityTypeName } from './types';

/**
 * Defensive client-side ceiling on the number of draft entities folded into
 * a single submission. Well under the backend's per-batch event limit; not a
 * security boundary — the server is the real enforcement point.
 */
export const MAX_DRAFT_ENTITIES = 50;

/**
 * Defensive client-side ceiling on the total number of events the drafts fold
 * into a single submission (one objectCreated per draft + one associationAdded
 * per draft association). The backend rejects any batch over 1000 events
 * (`EventBatchSerializer.events` max_length), so a small number of drafts each
 * carrying many associations could exceed the cap even while under
 * MAX_DRAFT_ENTITIES. This cap leaves headroom below 1000 for the parent's own
 * objectCreated + associationAdded events; the server remains the real
 * enforcement point.
 */
export const MAX_DRAFT_EVENTS = 900;

/**
 * The backend's hard per-batch event limit (`EventBatchSerializer.events`
 * max_length). The whole submission — the parent's objectCreated +
 * associationAdded events AND every folded draft event — must stay under this.
 * MAX_DRAFT_EVENTS alone is not sufficient: a parent carrying ~100+
 * associations can push a draft batch that is individually under
 * MAX_DRAFT_EVENTS over the backend cap, so callers pass their parent event
 * count and this guard enforces the combined total.
 */
export const MAX_BATCH_EVENTS = 1000;

/**
 * The backend's hard cap on `overwrite_targets` in a single submission
 * (`EventBatchSerializer.overwrite_targets` max_length). The parent form merges
 * its own acknowledged conflicts with those of every referenced draft, so a
 * submission with many acknowledgements can exceed this; enforce it here so an
 * over-cap payload fails fast client-side instead of being rejected by the
 * backend after an optimistic apply. The server remains the real enforcement
 * point.
 */
export const MAX_OVERWRITE_TARGETS = 100;

/**
 * Up-front guard for a draft batch. Call once before folding drafts into a
 * builder or applying any optimistic state — it runs before optimistic apply,
 * so a rejection surfaces to the caller without leaving partial store state.
 *
 * `parentEventCount` is the number of events the parent create itself will emit
 * into the same batch (its own objectCreated + associationAdded events). Pass it
 * so the combined parent + draft total is checked against the backend per-batch
 * cap; it defaults to 0 for callers that fold drafts with no parent events.
 *
 * Rejects when:
 *  - the batch exceeds MAX_DRAFT_ENTITIES, or
 *  - the drafts would emit more than MAX_DRAFT_EVENTS total events (an
 *    association-aware cap: many associations on few drafts can still blow the
 *    backend per-batch limit), or
 *  - the parent's own events plus the draft events would exceed
 *    MAX_BATCH_EVENTS (the backend hard cap — a large parent can push an
 *    otherwise-valid draft batch over the limit), or
 *  - any draft carries an `entity_type_name` that is not a valid object entity
 *    type. This is the same contract as `isValidEntityTypeName`, so reserved
 *    `*_timeline` identifiers (which the backend rejects and which would emit
 *    a nonsensical objectCreated event) are refused locally before folding.
 */
export function assertValidDraftEntities(
  drafts: readonly DraftEntity[],
  parentEventCount = 0
): void {
  if (drafts.length > MAX_DRAFT_ENTITIES) {
    throw new Error(
      `Too many draft entities: ${drafts.length} exceeds the limit of ${MAX_DRAFT_ENTITIES}`
    );
  }
  // objectCreated + associationAdded per draft — mirrors appendDraftEvents.
  const totalDraftEvents = drafts.reduce(
    (sum, draft) => sum + 1 + draft.associations.length,
    0
  );
  if (totalDraftEvents > MAX_DRAFT_EVENTS) {
    throw new Error(
      `Too many draft events: ${totalDraftEvents} exceeds the limit of ${MAX_DRAFT_EVENTS}`
    );
  }
  const totalBatchEvents = parentEventCount + totalDraftEvents;
  if (totalBatchEvents > MAX_BATCH_EVENTS) {
    throw new Error(
      `Too many events in batch: ${totalBatchEvents} (parent ${parentEventCount} + drafts ${totalDraftEvents}) exceeds the backend per-batch limit of ${MAX_BATCH_EVENTS}`
    );
  }
  for (const draft of drafts) {
    if (!isValidEntityTypeName(draft.entity_type_name)) {
      throw new Error(
        `Invalid draft entity_type_name: "${draft.entity_type_name}" is not a valid object entity type`
      );
    }
  }
}

export interface DraftParentContext {
  /** Parent form's entity type (e.g. 'ahoy_company'). */
  entityTypeName: string;
  /** Parent's display name at push time, if the user has typed one. */
  displayName?: string;
  /** reverse_association_type_name of the parent field the nested create was
   *  launched from (e.g. 'ahoy_deal_to_company'); this field is replaced by the banner. */
  reverseAssociationTypeName: string;
}

/** Parent-identity subset threaded from the parent form to its association fields. */
export type DraftParentInfo = Pick<
  DraftParentContext,
  'entityTypeName' | 'displayName'
>;

export interface DraftEntity {
  /** Client-minted UUID — becomes the entity_id of the objectCreated event. */
  entity_id: string;
  entity_type_name: string;
  /** Property values exactly as produced by the create form (post cleaning). */
  properties: Record<string, unknown>;
  /** The draft's own associations (e.g. a draft deal's pipeline stage, contacts). */
  associations: Array<{
    association_type_name: string;
    target_entity_id: string;
  }>;
  /**
   * Conflict acknowledgements the nested draft form captured when one of the
   * draft's associations targets an existing single-cardinality record. These
   * are NOT folded as events (appendDraftEvents ignores them); overwrite
   * authorization is batch-scoped, so the parent form merges them into the
   * submission's top-level `overwrite_targets`. Absent/empty when the draft
   * had no acknowledged conflicts.
   */
  overwrite_targets?: OverwriteTarget[];
}

/**
 * Fold draft entities into objectCreated + associationAdded events on the
 * given builder. Only READS fields off each draft — callers MUST snapshot
 * the drafts array at submit time (`[...drafts]`) so submitEventsOptimistic's
 * 409-retry rebuild replays event-identical. On an empty array this is a
 * no-op (emits zero events).
 */
/**
 * Merge overwrite-target lists into one deduped list. Authorization is scoped
 * per (entity_id, association_type_name) pair, so identical pairs from
 * different sources collapse to one. Used to fold each referenced draft's
 * acknowledged conflicts into the parent submission's top-level
 * `overwrite_targets` (overwrite authorization is batch-scoped).
 *
 * Throws when the merged unique count would exceed MAX_OVERWRITE_TARGETS — the
 * backend rejects any submission over that cap, so failing here surfaces the
 * error to the caller before the batch is submitted.
 */
export function mergeOverwriteTargets(
  ...lists: ReadonlyArray<readonly OverwriteTarget[]>
): OverwriteTarget[] {
  const seen = new Set<string>();
  const merged: OverwriteTarget[] = [];
  for (const list of lists) {
    for (const target of list) {
      const key = `${target.entity_id}|${target.association_type_name}`;
      if (!seen.has(key)) {
        if (merged.length >= MAX_OVERWRITE_TARGETS) {
          throw new Error(
            `Too many overwrite targets: ${merged.length + 1} exceeds the backend limit of ${MAX_OVERWRITE_TARGETS}`
          );
        }
        seen.add(key);
        merged.push(target);
      }
    }
  }
  return merged;
}

export function appendDraftEvents(
  builder: EventBuilder,
  drafts: readonly DraftEntity[]
): void {
  for (const draft of drafts) {
    // A draft is always a brand-new, client-minted entity, so its first event
    // MUST be seq 1 — independent of the builder's default start seq, which is
    // derived from the primary entity's persisted seq and is > 1 on the
    // matched-contact upsert path. Without this seed the folded objectCreated
    // would inherit the parent's continuation seq and the backend would reject
    // the new entity's first event as a sequence conflict.
    builder.seedEntitySeq(draft.entity_id, 1);
    builder.objectCreated(
      draft.entity_id,
      draft.entity_type_name,
      draft.properties
    );
    for (const assoc of draft.associations) {
      builder.associationAdded(
        draft.entity_id,
        draft.entity_type_name,
        assoc.association_type_name,
        assoc.target_entity_id
      );
    }
  }
}
